/**
 * Tutoriel du dashboard, branché sur le moteur commun (`components/tour`).
 *
 * Les démos resize/move/delete jouent sur un FAUX widget : on ne touche pas au vrai
 * layout. Seules les étapes « Ajouter » et « Ranger » ouvrent le vrai mode édition, d'où
 * leurs cibles marquées `keepWhenMissing` — elles n'existent pas au lancement.
 */
import { useCallback, useRef, useState } from "react";
import i18n from "../../../../i18n/i18n";
import { TourStep } from "../../../tour/tourEngine";
import { TourButton } from "../../../tour/TourButton";
import { useTour } from "../../../tour/useTour";
import { useDashboardEdit } from "../DashboardEditContext";
import { DeleteDemo } from "./DeleteDemo";
import { FakeWidget } from "./FakeWidget";
import { MoveDemo } from "./MoveDemo";
import { TutorialCursor } from "./TutorialCursor";

/** Sélecteurs des étapes qui affichent une scène animée. */
const STAGE_SELECTOR = "#tutorial-stage";
const MOVE_SELECTOR = "#tutorial-stage-move";
const DELETE_SELECTOR = "#tutorial-stage-delete";

/** Attend le montage de l'élément avant d'agir : driver le cadrerait sinon dans le vide. */
const whenPresent = (selector: string, action: () => void, tries = 20) => {
  if (document.querySelector(selector)) {
    action();
  } else if (tries > 0) {
    requestAnimationFrame(() => whenPresent(selector, action, tries - 1));
  } else {
    action();
  }
};

export function DashboardTutorial() {
  const [stageVisible, setStageVisible] = useState(false);
  const [moveVisible, setMoveVisible] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);

  // En ref pour garder le constructeur d'étapes stable.
  const { enterEditMode, exitEditMode } = useDashboardEdit();
  const enterEditModeRef = useRef(enterEditMode);
  enterEditModeRef.current = enterEditMode;
  const exitEditModeRef = useRef(exitEditMode);
  exitEditModeRef.current = exitEditMode;

  const buildSteps = useCallback((): TourStep[] => {
    // L'étape 2 cible le ✎, visible hors édition seulement.
    exitEditModeRef.current();

    return [
      {
        popover: {
          title: i18n.t("tour-dashboard-welcome-title"),
          description: i18n.t("tour-dashboard-welcome-text"),
        },
      },
      {
        element: "#dashboard-edit-fab",
        // Utile au retour arrière depuis l'étape « Ajouter ».
        onHighlightStarted: () => exitEditModeRef.current(),
        popover: {
          title: i18n.t("tour-dashboard-customize-title"),
          description: i18n.t("tour-dashboard-customize-text"),
          side: "left",
          align: "end",
          // On ouvre l'édition, puis on n'avance qu'une fois le + monté.
          onNextClick: (_element, _step, { driver }) => {
            enterEditModeRef.current();
            whenPresent("#dashboard-add-widget-fab", () => driver.moveNext());
          },
        },
      },
      {
        element: "#dashboard-add-widget-fab",
        data: { keepWhenMissing: true },
        onHighlightStarted: (_element, _step, { driver }) => {
          enterEditModeRef.current();
          whenPresent("#dashboard-add-widget-fab", () => driver.refresh());
        },
        popover: {
          title: i18n.t("tour-dashboard-add-title"),
          description: i18n.t("tour-dashboard-add-text"),
          side: "left",
          align: "end",
        },
      },
      {
        element: "#dashboard-auto-arrange-fab",
        data: { keepWhenMissing: true },
        onHighlightStarted: (_element, _step, { driver }) => {
          enterEditModeRef.current();
          whenPresent("#dashboard-auto-arrange-fab", () => driver.refresh());
        },
        // Les scènes suivantes ciblent le ✎ : on referme l'édition.
        onDeselected: () => exitEditModeRef.current(),
        popover: {
          title: i18n.t("tour-dashboard-arrange-title"),
          description: i18n.t("tour-dashboard-arrange-text"),
          side: "left",
          align: "end",
        },
      },
      {
        element: STAGE_SELECTOR,
        // Affiche la scène animée uniquement sur cette étape.
        onHighlighted: () => setStageVisible(true),
        popover: {
          title: i18n.t("tour-dashboard-resize-title"),
          description: i18n.t("tour-dashboard-resize-text"),
          side: "left",
          align: "center",
          // Même raison de cadrage, dans l'autre sens.
          onPrevClick: (_element, _step, { driver }) => {
            enterEditModeRef.current();
            whenPresent("#dashboard-add-widget-fab", () =>
              driver.movePrevious()
            );
          },
        },
      },
      {
        element: MOVE_SELECTOR,
        onHighlighted: () => setMoveVisible(true),
        popover: {
          title: i18n.t("tour-dashboard-move-title"),
          description: i18n.t("tour-dashboard-move-text"),
          side: "top",
          align: "center",
        },
      },
      {
        element: DELETE_SELECTOR,
        onHighlighted: () => setDeleteVisible(true),
        popover: {
          title: i18n.t("tour-dashboard-delete-title"),
          description: i18n.t("tour-dashboard-delete-text"),
          side: "left",
          align: "center",
        },
      },
      {
        popover: {
          title: i18n.t("tour-dashboard-end-title"),
          description: i18n.t("tour-dashboard-end-text"),
        },
      },
    ];
  }, []);

  const hideStages = useCallback(() => {
    setStageVisible(false);
    setMoveVisible(false);
    setDeleteVisible(false);
  }, []);

  const { start } = useTour({
    id: "dashboard",
    steps: buildSteps,
    // Seul tutoriel de page joué aussi pour l'admin : comportement d'avant l'intégration.
    autoStart: true,
    config: {
      // Cadrage collé aux scènes animées.
      stagePadding: 0,
      stageRadius: 16,
      onHighlightStarted: hideStages,
      onDestroyed: () => {
        hideStages();
        exitEditModeRef.current();
      },
    },
  });

  return (
    <>
      <TourButton onClick={start} />

      {/* Toujours montées (ce sont les cibles), animées seulement pendant leur étape. */}
      <div
        id="tutorial-stage"
        aria-hidden="true"
        className={`tutorial-stage ${stageVisible ? "is-visible" : ""}`}
      >
        <FakeWidget running={stageVisible} />
        <TutorialCursor running={stageVisible} />
      </div>

      <div
        id="tutorial-stage-move"
        aria-hidden="true"
        className={`tutorial-stage tutorial-stage--move ${
          moveVisible ? "is-visible" : ""
        }`}
      >
        <MoveDemo running={moveVisible} />
      </div>

      <div
        id="tutorial-stage-delete"
        aria-hidden="true"
        className={`tutorial-stage tutorial-stage--delete ${
          deleteVisible ? "is-visible" : ""
        }`}
      >
        <DeleteDemo running={deleteVisible} />
      </div>
    </>
  );
}
